import type {
  CanvasSectionBlock,
  CanvasSectionBlockLayout,
  HomepageLayoutContent,
} from '../components/sections/sectionRegistry';
import { vases } from '../data/vases';
import { toWebpVariantUrl } from './optimizedImages';
import {
  createPageLayout,
  listPageLayoutsForAdmin,
  loadPageLayoutForAdmin,
  publishPageLayoutDraft,
  resolvePageLayoutDraft,
  savePageLayoutDraft,
} from './pageLayoutsDraft';

export const SNAP_FIXTURE_PAGE_KEY = 'snap-fixture';
export const SNAP_FIXTURE_PAGE_TITLE = 'Snap fixture (debug)';

export const SNAP_FIXTURE_DRAG_TARGET_ID = 'snap-fixture-drag-target';

export const SNAP_FIXTURE_SCENARIOS = [
  {
    id: 'free-drag',
    title: 'Free drag',
    detail: 'Drag DRAG ME in row A. Watch grey gap pills, purple equal pairs, orange rhythm.',
  },
  {
    id: 'alt',
    title: 'Alt — no magnetic snap',
    detail: 'Hold Alt while dragging. Magnetic pull turns off (gap labels may still show).',
  },
  {
    id: 'duplicate',
    title: 'Ctrl+Alt — duplicate drag',
    detail: 'Ctrl+Alt+drag (Cmd+Alt on Mac) duplicates the block without turning snap off.',
  },
  {
    id: 'shift',
    title: 'Shift — axis lock',
    detail: 'Hold Shift after settling on an axis. The settled axis should stay fixed.',
  },
  {
    id: 'rhythm',
    title: 'Row A rhythm (~68px)',
    detail: 'Drag DRAG ME horizontally in row A. Orange should match row gaps, not whole-page noise.',
  },
  {
    id: 'wide-row',
    title: 'Row B wider gaps',
    detail: 'Drag a row B button. Competing rhythm from row A should not dominate.',
  },
  {
    id: 'equal',
    title: 'Equal spacing trio',
    detail: 'Drag the middle equal-spacing button. Purple on adjacent gaps only.',
  },
  {
    id: 'close',
    title: 'Close snap targets',
    detail: 'Drag between the two tight buttons at the bottom. Snap should pick the nearest edge.',
  },
  {
    id: 'column',
    title: 'Column alignment',
    detail: 'Drag blocks in the right column. Vertical edge guides should win over glow noise.',
  },
  {
    id: 'images',
    title: 'Image grid',
    detail: 'Drag an image tile. Gap and edge snap amid mixed content sizes.',
  },
] as const;

type SnapFixtureComposition = 'desktop' | 'tablet' | 'mobile';

const desktopLayouts = {
  'snap-fixture-title': { x: 2, y: 1, w: 14, h: 1 },
  'snap-fixture-subtitle': { x: 2, y: 2, w: 16, h: 1 },
  'snap-fixture-label-row-a': { x: 2, y: 3, w: 12, h: 1 },
  'snap-fixture-row-a-1': { x: 3, y: 4, w: 3, h: 1 },
  'snap-fixture-drag-target': { x: 6, y: 4, w: 4, h: 1 },
  'snap-fixture-row-a-3': { x: 10, y: 4, w: 3, h: 1 },
  'snap-fixture-label-row-b': { x: 2, y: 5, w: 12, h: 1 },
  'snap-fixture-row-b-1': { x: 3, y: 6, w: 3, h: 1 },
  'snap-fixture-row-b-2': { x: 9, y: 6, w: 3, h: 1 },
  'snap-fixture-row-b-3': { x: 15, y: 6, w: 3, h: 1 },
  'snap-fixture-label-images': { x: 2, y: 7, w: 10, h: 1 },
  'snap-fixture-img-1': { x: 3, y: 8, w: 3, h: 2 },
  'snap-fixture-img-2': { x: 7, y: 8, w: 3, h: 2 },
  'snap-fixture-img-3': { x: 11, y: 8, w: 3, h: 2 },
  'snap-fixture-label-equal': { x: 2, y: 10, w: 14, h: 1 },
  'snap-fixture-equal-1': { x: 3, y: 11, w: 3, h: 1 },
  'snap-fixture-equal-2': { x: 7, y: 11, w: 3, h: 1 },
  'snap-fixture-equal-3': { x: 11, y: 11, w: 3, h: 1 },
  'snap-fixture-label-text': { x: 2, y: 12, w: 8, h: 1 },
  'snap-fixture-text-1': { x: 2, y: 13, w: 5, h: 1 },
  'snap-fixture-text-2': { x: 2, y: 14, w: 6, h: 2 },
  'snap-fixture-text-3': { x: 2, y: 16, w: 4, h: 1 },
  'snap-fixture-label-close': { x: 2, y: 17, w: 12, h: 1 },
  'snap-fixture-close-left': { x: 3, y: 18, w: 2, h: 1 },
  'snap-fixture-close-right': { x: 5, y: 18, w: 2, h: 1 },
  'snap-fixture-label-column': { x: 17, y: 11, w: 6, h: 1 },
  'snap-fixture-col-1': { x: 18, y: 12, w: 3, h: 1 },
  'snap-fixture-col-2': { x: 18, y: 13, w: 3, h: 1 },
  'snap-fixture-col-3': { x: 18, y: 14, w: 3, h: 1 },
  'snap-fixture-glow': { x: 15, y: 3, w: 8, h: 12 },
} satisfies Record<string, CanvasSectionBlockLayout>;

const SNAP_FIXTURE_BLOCK_LAYOUTS: Record<
  SnapFixtureComposition,
  Record<string, CanvasSectionBlockLayout>
> = {
  desktop: desktopLayouts,
  tablet: { ...desktopLayouts },
  mobile: { ...desktopLayouts },
};

function fixtureResponsiveLayout(blockId: string) {
  return {
    tablet: SNAP_FIXTURE_BLOCK_LAYOUTS.tablet[blockId],
    mobile: SNAP_FIXTURE_BLOCK_LAYOUTS.mobile[blockId],
  };
}

function fixtureLabelStyle() {
  return {
    fontFamilyKey: 'dmSans',
    fontSize: 10,
    fontSizeDesktop: 10,
    fontSizeMobile: 10,
    fontWeight: 500,
    letterSpacing: 0.12,
    textColor: '#9a8b6d',
    textOpacity: 0.85,
    textAlign: 'left' as const,
    zIndex: 120,
  };
}

function fixtureButtonStyle(highlight = false) {
  return {
    backgroundColor: '#000000',
    backgroundOpacity: highlight ? 0.08 : 0,
    borderColor: highlight ? '#d6b36d' : '#a98752',
    borderWidth: highlight ? 2 : 1,
    borderStyle: 'solid' as const,
    borderRadius: 12,
    sizingMode: 'fit' as const,
    visualHeight: 46,
    alignX: 'center' as const,
    alignY: 'center' as const,
    paddingX: highlight ? 18 : 16,
    paddingY: 0,
    textColor: highlight ? '#f2e7ca' : '#c9a766',
    fontFamilyKey: 'dmSans',
    fontSize: 11,
    fontSizeDesktop: 11,
    fontSizeMobile: 11,
    fontWeight: 500,
    letterSpacing: 1.4,
    textTransform: 'uppercase' as const,
    zIndex: highlight ? 110 : 100,
    hover: {
      backgroundColor: '#d6b36d',
      backgroundOpacity: 0.055,
      borderColor: '#d6b36d',
      textColor: '#f2e7ca',
    },
  };
}

function fixtureButton(
  id: string,
  label: string,
  highlight = false
): CanvasSectionBlock {
  return {
    id,
    type: 'button',
    layout: SNAP_FIXTURE_BLOCK_LAYOUTS.desktop[id],
    responsiveLayout: fixtureResponsiveLayout(id),
    content: {
      label,
      actionMode: 'simple',
      actionKind: 'navigation',
      pageKey: 'home',
    },
    style: fixtureButtonStyle(highlight),
  };
}

function fixtureLabel(id: string, text: string): CanvasSectionBlock {
  return {
    id,
    type: 'text',
    layout: SNAP_FIXTURE_BLOCK_LAYOUTS.desktop[id],
    responsiveLayout: fixtureResponsiveLayout(id),
    content: {
      text,
      presetKey: 'caption',
    },
    style: fixtureLabelStyle(),
  };
}

function fixtureImage(id: string, src: string, alt: string): CanvasSectionBlock {
  return {
    id,
    type: 'image',
    layout: SNAP_FIXTURE_BLOCK_LAYOUTS.desktop[id],
    responsiveLayout: fixtureResponsiveLayout(id),
    content: {
      src,
      alt,
    },
    style: {
      borderRadius: 8,
      zIndex: 90,
    },
  };
}

const fixtureImages = vases
  .slice(0, 3)
  .map((vase, index) => ({
    id: `snap-fixture-img-${index + 1}`,
    src: toWebpVariantUrl(vase.image, 'md'),
    alt: `${vase.name} fixture image`,
  }))
  .filter((item) => item.src.trim().length > 0);

export const DEFAULT_SNAP_FIXTURE_LAYOUT_CONTENT: HomepageLayoutContent = {
  version: 1,
  flows: [],
  flowState: {},
  sections: [
    {
      type: 'canvas',
      id: 'snap-fixture-stage',
      settings: {
        layoutMode: 'composition',
        padding: 'none',
        paddingTop: 'none',
        paddingBottom: 'none',
        maxWidth: 'full',
        background: 'default',
        canvas: {
          minRows: 20,
          minRowsMode: 'manual',
          blocks: [
            fixtureLabel(
              'snap-fixture-title',
              'Snap fixture — drag blocks to test alignment, gaps, and magnetic snap'
            ),
            {
              id: 'snap-fixture-subtitle',
              type: 'text',
              layout: SNAP_FIXTURE_BLOCK_LAYOUTS.desktop['snap-fixture-subtitle'],
              responsiveLayout: fixtureResponsiveLayout('snap-fixture-subtitle'),
              content: {
                text: 'Add ?movementDebug=1 to the URL to capture exportable drag traces.',
                presetKey: 'caption',
              },
              style: {
                ...fixtureLabelStyle(),
                textColor: '#7f9ab0',
                textOpacity: 0.9,
              },
            },
            fixtureLabel(
              'snap-fixture-label-row-a',
              'Row A · holding-like rhythm (~68px) — primary test row'
            ),
            fixtureButton('snap-fixture-row-a-1', 'ALPHA'),
            fixtureButton('snap-fixture-drag-target', 'DRAG ME', true),
            fixtureButton('snap-fixture-row-a-3', 'GAMMA'),
            fixtureLabel(
              'snap-fixture-label-row-b',
              'Row B · wider horizontal gaps (~96px+) — competing rhythm'
            ),
            fixtureButton('snap-fixture-row-b-1', 'ONE'),
            fixtureButton('snap-fixture-row-b-2', 'TWO'),
            fixtureButton('snap-fixture-row-b-3', 'THREE'),
            fixtureLabel('snap-fixture-label-images', 'Image grid — mixed block sizes'),
            ...(fixtureImages.length >= 3
              ? fixtureImages.map((item) => fixtureImage(item.id, item.src, item.alt))
              : [
                  fixtureImage('snap-fixture-img-1', '', 'Fixture image 1'),
                  fixtureImage('snap-fixture-img-2', '', 'Fixture image 2'),
                  fixtureImage('snap-fixture-img-3', '', 'Fixture image 3'),
                ]),
            fixtureLabel(
              'snap-fixture-label-equal',
              'Equal spacing trio — drag middle button for purple gap pairs'
            ),
            fixtureButton('snap-fixture-equal-1', 'LEFT'),
            fixtureButton('snap-fixture-equal-2', 'MIDDLE'),
            fixtureButton('snap-fixture-equal-3', 'RIGHT'),
            fixtureLabel('snap-fixture-label-text', 'Text noise — edge alignment clutter'),
            {
              id: 'snap-fixture-text-1',
              type: 'text',
              layout: SNAP_FIXTURE_BLOCK_LAYOUTS.desktop['snap-fixture-text-1'],
              responsiveLayout: fixtureResponsiveLayout('snap-fixture-text-1'),
              content: {
                text: 'Short label block',
                presetKey: 'caption',
              },
              style: {
                fontFamilyKey: 'dmSans',
                fontSize: 12,
                textColor: '#c9a766',
                zIndex: 100,
              },
            },
            {
              id: 'snap-fixture-text-2',
              type: 'text',
              layout: SNAP_FIXTURE_BLOCK_LAYOUTS.desktop['snap-fixture-text-2'],
              responsiveLayout: fixtureResponsiveLayout('snap-fixture-text-2'),
              content: {
                text: 'Wider paragraph block creates extra horizontal edges when you drag nearby elements.',
                presetKey: 'paragraph',
              },
              style: {
                fontFamilyKey: 'dmSans',
                fontSize: 13,
                lineHeight: 1.45,
                textColor: '#d0b073',
                textOpacity: 0.88,
                zIndex: 100,
              },
            },
            {
              id: 'snap-fixture-text-3',
              type: 'text',
              layout: SNAP_FIXTURE_BLOCK_LAYOUTS.desktop['snap-fixture-text-3'],
              responsiveLayout: fixtureResponsiveLayout('snap-fixture-text-3'),
              content: {
                text: 'Tiny caption',
                presetKey: 'caption',
              },
              style: {
                fontFamilyKey: 'dmSans',
                fontSize: 10,
                textColor: '#9a8b6d',
                zIndex: 100,
              },
            },
            fixtureLabel(
              'snap-fixture-label-close',
              'Close targets — two buttons share grid columns (snap competition)'
            ),
            fixtureButton('snap-fixture-close-left', 'NEAR-L'),
            fixtureButton('snap-fixture-close-right', 'NEAR-R'),
            fixtureLabel('snap-fixture-label-column', 'Right column — vertical edge stack'),
            fixtureButton('snap-fixture-col-1', 'COL 1'),
            fixtureButton('snap-fixture-col-2', 'COL 2'),
            fixtureButton('snap-fixture-col-3', 'COL 3'),
            {
              id: 'snap-fixture-glow',
              type: 'spacer',
              layout: SNAP_FIXTURE_BLOCK_LAYOUTS.desktop['snap-fixture-glow'],
              responsiveLayout: fixtureResponsiveLayout('snap-fixture-glow'),
              style: {
                backgroundColor: '#000000',
                backgroundOpacity: 0,
                backgroundImage:
                  'radial-gradient(ellipse at 50% 48%, rgba(255, 255, 255, 0.035) 0%, rgba(255, 255, 255, 0.01) 28%, transparent 62%)',
                filter: 'blur(9px)',
                mixBlendMode: 'screen',
                zIndex: 1,
              },
            },
          ],
        },
      },
    },
  ],
};

function snapFixtureLayoutHasMarker(layout: HomepageLayoutContent | null | undefined) {
  const section = layout?.sections?.find((entry) => entry.type === 'canvas');
  const blocks = section?.settings?.canvas?.blocks;
  if (!Array.isArray(blocks)) return false;
  return blocks.some((block) => block?.id === SNAP_FIXTURE_DRAG_TARGET_ID);
}

function snapFixtureNeedsRepair(layout: HomepageLayoutContent | null | undefined) {
  if (!snapFixtureLayoutHasMarker(layout)) return true;
  const section = layout?.sections?.find((entry) => entry.type === 'canvas');
  return section?.settings?.canvas?.presentation === 'immersive';
}

let ensureSnapFixturePagePromise: Promise<void> | null = null;

export async function ensureSnapFixturePageLayout() {
  if (ensureSnapFixturePagePromise) return ensureSnapFixturePagePromise;

  ensureSnapFixturePagePromise = (async () => {
    const pagesRes = await listPageLayoutsForAdmin();
    if (pagesRes.error) throw pagesRes.error;

    const exists = (pagesRes.data ?? []).some((row) => row.page_key === SNAP_FIXTURE_PAGE_KEY);
    if (!exists) {
      const { error } = await createPageLayout({
        pageKey: SNAP_FIXTURE_PAGE_KEY,
        title: SNAP_FIXTURE_PAGE_TITLE,
        draftContent: DEFAULT_SNAP_FIXTURE_LAYOUT_CONTENT,
        publishedContent: DEFAULT_SNAP_FIXTURE_LAYOUT_CONTENT,
      });

      if (error) {
        const message = String((error as any)?.message ?? '').toLowerCase();
        if (!message.includes('already exists') && !message.includes('duplicate')) {
          throw error;
        }
      }
      return;
    }

    const loadRes = await loadPageLayoutForAdmin(SNAP_FIXTURE_PAGE_KEY);
    if (loadRes.error || !loadRes.data) {
      if (loadRes.error) throw loadRes.error;
      return;
    }

    const currentDraft = resolvePageLayoutDraft(
      loadRes.data,
      DEFAULT_SNAP_FIXTURE_LAYOUT_CONTENT
    );
    if (!snapFixtureNeedsRepair(currentDraft)) return;

    const saveRes = await savePageLayoutDraft(
      SNAP_FIXTURE_PAGE_KEY,
      DEFAULT_SNAP_FIXTURE_LAYOUT_CONTENT
    );
    if (saveRes.error) throw saveRes.error;

    const publishRes = await publishPageLayoutDraft(SNAP_FIXTURE_PAGE_KEY);
    if ((publishRes as { error?: unknown })?.error) {
      throw (publishRes as { error: unknown }).error;
    }
  })();

  try {
    await ensureSnapFixturePagePromise;
  } finally {
    ensureSnapFixturePagePromise = null;
  }
}
